import { Chip } from "@mui/material";
import { ValidateFunction } from "ajv";
import { Autocomplete } from "components/Form/components/Autocomplete/Autocomplete";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import { LabelledFormElement } from "components/Form/components/SchemaForm/LabelledFormElement";
import {
    extendObjectPath,
    fieldContainerClassName,
    getDefaultRequiredValue,
    getIsOptional,
    getSchemaFormErrorMessage,
    getUpdatedData,
} from "components/Form/components/SchemaForm/SchemaFormService";
import {
    SchemaFormActionButtons,
    SchemaFormShownActionButtons,
    unsetValue,
} from "components/Form/components/SchemaForm/components/components";
import {
    SchemaFormFragment,
    SchemaFormFragmentProps,
} from "components/Form/components/SchemaForm/fragments/SchemaFormFragment";
import { isObject } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

export interface SchemaFormDropdownProps extends SchemaFormFragmentProps {
    elementSchema: any; // TODO verify
    elementData: object;
    shownButtons: SchemaFormShownActionButtons;
}

// Cache one compiled validator per anyOf/oneOf sub-schema, keyed by the schema
// object's identity. The sub-schema references are stable across renders, so a
// WeakMap both avoids recompiling on every render and lets entries be garbage
// collected when the schema is gone (the previous @mootable/hashmap keyed on a
// cloneDeep'd object hashed by identity → always missed and never released). See
// CU 86camf0px.
const validatorMap = new WeakMap<object, ValidateFunction<unknown>>();

export const SchemaFormDropdown = ({
    elementSchema,
    elementData,
    schema,
    schemaPath,
    formData,
    dataPath,
    setData,
    isReadOnly,
    showLabel,
    isRequired,
    isStyleRequired,
    isRecordDisabled,
    errorData,
    isNewRecord,
    ajv,
    shownButtons,
    omitRequired,
}: SchemaFormDropdownProps) => {
    const elementSchemaOneOf = elementSchema.oneOf ?? elementSchema.anyOf;
    const selectedBranchRef = useRef<number | null>(null);

    useEffect(() => {
        if (elementData === undefined) {
            selectedBranchRef.current = null;
        }
    }, [elementData]);

    const validateBranch = useCallback(
        (index: number, data: unknown) => {
            const branchSchema = elementSchemaOneOf[index];
            if (branchSchema == null) {
                return false;
            }
            try {
                let validator = validatorMap.get(branchSchema);
                if (validator == null) {
                    validator = ajv.compile(branchSchema) as ValidateFunction<unknown>;
                    validatorMap.set(branchSchema, validator);
                }
                return validator(data);
            } catch (error) {
                console.warn("validate failed", error);
                return false;
            }
        },
        [ajv, elementSchemaOneOf]
    );

    // Prefer objectIdentifier or a sticky branch that still validates, so overlapping
    // anyOf/oneOf options do not flip the sub-form on every keystroke.
    const selectedIndex = useMemo(() => {
        if (elementData === undefined) {
            return -1;
        }

        const findIndexUsingAjv = () =>
            elementSchemaOneOf.findIndex((_, index) => validateBranch(index, elementData));

        if (
            elementSchema.objectIdentifier != null &&
            isObject(elementData) &&
            elementData[elementSchema.objectIdentifier]
        ) {
            const identifierIndex = elementSchemaOneOf.findIndex((anyOfElement) => {
                if (
                    anyOfElement.element !== "form-object" &&
                    anyOfElement.element !== "form-optional-object"
                ) {
                    return false;
                }
                const identifierProperty =
                    anyOfElement.properties?.[elementSchema.objectIdentifier];
                if (!identifierProperty) {
                    return false;
                }
                if (identifierProperty.element !== "form-const-string") {
                    return false;
                }
                return identifierProperty.const === elementData[elementSchema.objectIdentifier];
            });
            if (identifierIndex > -1) {
                selectedBranchRef.current = identifierIndex;
                return identifierIndex;
            }
        }

        if (
            selectedBranchRef.current !== null &&
            validateBranch(selectedBranchRef.current, elementData)
        ) {
            return selectedBranchRef.current;
        }

        const index = findIndexUsingAjv();
        if (index >= 0) {
            selectedBranchRef.current = index;
        }
        return index;
    }, [
        elementData,
        elementSchema.objectIdentifier,
        elementSchemaOneOf,
        validateBranch,
    ]);

    const onChange = (value) => {
        const index = parseInt(value ?? elementSchema.defaultIndex ?? 0, 10);
        selectedBranchRef.current = index;
        const itemSchema = elementSchemaOneOf[index];
        const leDefault = getDefaultRequiredValue(schema, itemSchema, null);
        setData(getUpdatedData(formData, dataPath, leDefault));
    };

    const errorMessage = getSchemaFormErrorMessage(dataPath, errorData);

    const descriptionElement = useMemo(() => {
        if (elementSchemaOneOf && elementSchemaOneOf[selectedIndex]) {
            const description = elementSchemaOneOf[selectedIndex]?.description || null;
            if (description == null) return null;
            return <Markdown markdownString={description} className="description" />;
        }
        return null;
    }, [elementSchemaOneOf, selectedIndex]);

    // Read-only display (custom-object list cells, dashboard record-table
    // widgets, disabled/deleted records, …): render the selected enum value as a
    // small non-interactive badge instead of a disabled dropdown, which reads as
    // a clickable button in list-view cells. Mirrors the read-only plain-text
    // branch used for string/text attributes (SchemaFormFragment form-string).
    // Gated on `isReadOnly` only — NOT `isRecordDisabled`: the inline-editable
    // list dropdown (EditableAttributeValueSchemaDTO) is interactive with
    // isReadOnly=false but isRecordDisabled=true, and must keep its live control.
    // An unset/null value renders as an empty cell rather than a badge.
    if (isReadOnly) {
        const selectedOption =
            selectedIndex >= 0 ? elementSchemaOneOf[selectedIndex] : undefined;
        // A composite variant (object/array/tuple/nested dropdown) carries its own
        // descendant sub-form. Render that sub-form read-only so the value is shown,
        // instead of collapsing the whole branch to an (empty) badge — the badge is
        // only meaningful for a scalar enum/const variant. Without this, a
        // form-dropdown/anyOf whose value-matched branch is e.g. a form-object (a
        // connector's output_value schema, which is anyOf[null, object]) renders
        // empty on every read-only surface — the execution Output tab "Form view".
        const isComposite =
            selectedOption != null &&
            (selectedOption.properties != null ||
                selectedOption.items != null ||
                selectedOption.prefixItems != null ||
                selectedOption.anyOf != null ||
                selectedOption.oneOf != null);
        if (isComposite && selectedIndex !== -1) {
            return (
                <LabelledFormElement
                    label={showLabel ? elementSchema.label : ""}
                    description={showLabel ? elementSchema.description : ""}
                    elementSchemaStyle={elementSchema.style}
                >
                    <div className="dropdownDescendants">
                        <SchemaFormFragment
                            isRoot={false}
                            isReadOnly={true}
                            schema={schema}
                            schemaPath={extendObjectPath(
                                schemaPath,
                                elementSchema.oneOf ? "oneOf" : "anyOf",
                                String(selectedIndex)
                            )}
                            formData={formData}
                            dataPath={dataPath}
                            setData={setData}
                            showLabel={false}
                            errorData={errorData}
                            isRecordDisabled={isRecordDisabled}
                            isNewRecord={isNewRecord}
                            ajv={ajv}
                            omitRequired={omitRequired}
                        />
                    </div>
                </LabelledFormElement>
            );
        }
        const isNullOption =
            selectedOption?.element === "form-const-null" || selectedOption?.type === "null";
        const badgeLabel = isNullOption
            ? undefined
            : selectedOption?.label ?? selectedOption?.const;
        return (
            <LabelledFormElement
                label={showLabel ? elementSchema.label : ""}
                description={showLabel ? elementSchema.description : ""}
                elementSchemaStyle={elementSchema.style}
            >
                {badgeLabel != null && badgeLabel !== "" ? (
                    <Chip
                        className="schemaFormReadonlyBadge"
                        label={String(badgeLabel)}
                        size="small"
                    />
                ) : (
                    <div className="schemaFormReadonlyText" />
                )}
            </LabelledFormElement>
        );
    }

    return (
        <LabelledFormElement
            label={showLabel ? elementSchema.label : ""}
            description={elementSchema.description}
            labelClassName={`${showLabel && (isStyleRequired ?? isRequired) ? "isRequired" : ""}`}
            isRemoved={elementData === undefined && !(isStyleRequired ?? isRequired)}
            onAdd={() => {
                if (isRecordDisabled) return;
                onChange(elementSchema.defaultIndex ?? 0);
            }}
            errorMessage={errorMessage}
            elementSchemaStyle={elementSchema.style}
        >
            <div className={fieldContainerClassName(errorMessage)}>
                <Autocomplete
                    name={""}
                    options={{
                        list: elementSchema
                            ? elementSchemaOneOf.map((item, index) => {
                                  return {
                                      value: index,
                                      label: item.label ?? item.const,
                                  };
                              })
                            : [],
                    }}
                    onChange={onChange}
                    value={
                        elementData === undefined ? elementSchema.defaultIndex ?? 0 : selectedIndex
                    }
                    isDisabled={isReadOnly}
                    additionalProps={{
                        clearable: false,
                        isDropdown: true,
                        customKey: JSON.stringify(schemaPath),
                        fieldWidth: "100%",
                        maxFieldWidth: 420,
                    }}
                />
                {isRecordDisabled ? null : (
                    <SchemaFormActionButtons
                        value={
                            elementData === null
                                ? unsetValue
                                : elementSchemaOneOf[selectedIndex]?.label
                        }
                        defaultValue={
                            elementData === null
                                ? elementSchemaOneOf[elementSchema.defaultIndex ?? 0].type ===
                                  "null"
                                    ? null
                                    : elementSchemaOneOf[elementSchema.defaultIndex ?? 0].label
                                : // : elementSchemaOneOf[elementSchema.defaultIndex].default ??
                                  //   elementSchemaOneOf[elementSchema.defaultIndex].const
                                  elementSchemaOneOf[elementSchema.defaultIndex ?? 0].label
                        }
                        isRequired={isRequired}
                        unsetField={() => setData(getUpdatedData(formData, dataPath, null))}
                        resetToDefault={() => onChange(elementSchema.defaultIndex ?? 0)}
                        removeField={() => setData(getUpdatedData(formData, dataPath, undefined))}
                        isOptional={getIsOptional(elementSchema.element)}
                        isSetToDefaultAllowed={true}
                        shownButtons={shownButtons}
                    />
                )}
            </div>
            {descriptionElement && (
                <div
                    className="labelAndDescription"
                    style={{ marginTop: "5px", marginLeft: "5px" }}
                >
                    <div className="description">{descriptionElement}</div>
                </div>
            )}
            {selectedIndex !== -1 && (
                <div className="dropdownDescendants">
                    <SchemaFormFragment
                        isRoot={false}
                        isReadOnly={isReadOnly}
                        schema={schema}
                        schemaPath={extendObjectPath(
                            schemaPath,
                            elementSchema.oneOf ? "oneOf" : "anyOf",
                            String(selectedIndex)
                        )}
                        formData={formData}
                        dataPath={dataPath}
                        setData={setData}
                        showLabel={false}
                        errorData={errorData}
                        isRecordDisabled={isRecordDisabled}
                        isNewRecord={isNewRecord}
                        ajv={ajv}
                        omitRequired={omitRequired}
                    />
                </div>
            )}
        </LabelledFormElement>
    );
};
